import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, ArrowUpRight, ArrowDownLeft } from 'lucide-react';

const APPT_CHIP = {
  'Needs Scheduling': { bg: '#fde3e9', fg: '#b3003b' },
  'Requested': { bg: '#fdf3d5', fg: '#8a6d00' },
  'Granted': { bg: '#dcf3e4', fg: '#0c6b33' },
  'Updated TMS': { bg: '#f2f4f9', fg: '#495057' },
  'Change': { bg: '#fde9d5', fg: '#a34e00' },
};

export const TodaySchedule = ({ loads }) => {
  const navigate = useNavigate();

  const items = useMemo(() => {
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
    const out = [];
    (loads || []).forEach((load) => {
      (load.stops || []).forEach((stop) => {
        if (!stop.appointment_datetime) return;
        const dt = new Date(stop.appointment_datetime);
        if (dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === d) {
          out.push({
            loadId: load.id,
            loadNumber: load.load_number || load.id.substring(0, 8),
            customer: load.customer_name,
            carrier: load.carrier_name,
            type: stop.type,
            facility: stop.facility_name,
            city: stop.city,
            state: stop.state,
            time: dt,
            apptStatus: stop.appointment_status || 'Needs Scheduling',
          });
        }
      });
    });
    return out.sort((a, b) => a.time - b.time);
  }, [loads]);

  return (
    <div className="stats-card mb-4 sm:mb-6 lg:mb-8" data-testid="today-schedule-widget">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-[var(--tms-text)] flex items-center gap-2">
          <CalendarClock className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--tms-primary)]" />
          Today's Schedule
        </h3>
        <span className="text-xs sm:text-sm text-[var(--tms-text-muted)]">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} • {items.length} stop{items.length === 1 ? '' : 's'}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs sm:text-sm text-[var(--tms-text-muted)] text-center py-4">No pickups or deliveries scheduled for today</p>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="border-b border-[var(--tms-border)]">
                <th className="text-left py-2 px-3 text-xs font-bold uppercase text-[var(--tms-text-secondary)]">Time</th>
                <th className="text-left py-2 px-3 text-xs font-bold uppercase text-[var(--tms-text-secondary)]">Type</th>
                <th className="text-left py-2 px-3 text-xs font-bold uppercase text-[var(--tms-text-secondary)]">Load</th>
                <th className="text-left py-2 px-3 text-xs font-bold uppercase text-[var(--tms-text-secondary)]">Facility</th>
                <th className="text-left py-2 px-3 text-xs font-bold uppercase text-[var(--tms-text-secondary)]">Customer</th>
                <th className="text-left py-2 px-3 text-xs font-bold uppercase text-[var(--tms-text-secondary)]">Appt Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const chip = APPT_CHIP[it.apptStatus] || APPT_CHIP['Updated TMS'];
                return (
                  <tr
                    key={`${it.loadId}-${it.type}-${it.time.getTime()}-${i}`}
                    className="border-b border-[var(--tms-border)]/50 cursor-pointer hover:bg-[var(--tms-hover)] transition-colors"
                    onClick={() => navigate(`/loads/${it.loadId}`)}
                    data-testid={`today-stop-${it.loadNumber}-${it.type}`}
                  >
                    <td className="py-2 px-3 text-xs sm:text-sm font-bold whitespace-nowrap">
                      {it.time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </td>
                    <td className="py-2 px-3">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: it.type === 'pickup' ? '#0a3a75' : '#0c6b33' }}>
                        {it.type === 'pickup' ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownLeft className="w-3.5 h-3.5" />}
                        {it.type === 'pickup' ? 'PICKUP' : 'DELIVERY'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs sm:text-sm font-mono text-[var(--tms-primary)]">{it.loadNumber}</td>
                    <td className="py-2 px-3 text-xs sm:text-sm truncate max-w-[180px]">{it.facility}{it.city ? ` — ${it.city}, ${it.state || ''}` : ''}</td>
                    <td className="py-2 px-3 text-xs sm:text-sm truncate max-w-[140px]">{it.customer}</td>
                    <td className="py-2 px-3">
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded" style={{ background: chip.bg, color: chip.fg }}>
                        {it.apptStatus}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
